import { Component } from '@theme/component';
import { CartLinesUpdateEvent } from '@shopify/events';

export function engravingProperties(original, values, count, font) {
  const properties = { ...original };
  for (let index = 1; index <= count; index++) properties[`Engraving line ${index}`] = String(values[index - 1] || '').trim();
  const hasText = values.some(value => String(value || '').trim());
  properties['Engraving font'] = hasText ? (font || original?.['Engraving font'] || 'Lato') : '';
  return properties;
}

class PawtureCartEditor extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.dialog = this.querySelector('dialog');
    this.form = this.querySelector('form');
    document.addEventListener('click', (event) => {
      const trigger = event.target instanceof Element ? event.target.closest('[data-pw-cart-edit]') : null;
      if (trigger) this.openEditor(trigger);
    }, { signal });
    this.querySelector('[data-pw-editor-close]').addEventListener('click', () => this.dialog.close(), { signal });
    this.dialog.addEventListener('cancel', (event) => { if (this.busy) event.preventDefault(); }, { signal });
    this.dialog.addEventListener('close', () => this.trigger?.focus(), { signal });
    this.form.addEventListener('submit', (event) => { event.preventDefault(); this.save(); }, { signal });
  }

  disconnectedCallback() {
    this.controller?.abort();
    super.disconnectedCallback();
  }

  openEditor(trigger) {
    if (this.busy) return;
    this.trigger = trigger;
    this.lineKey = trigger.dataset.pwKey;
    this.lineCount = Number(trigger.dataset.pwLines) || 2;
    this.querySelector('[data-pw-editor-error]').textContent = '';
    const properties = JSON.parse(trigger.dataset.pwProperties || '{}');
    let fonts;
    try { fonts = JSON.parse(trigger.dataset.pwFonts || 'null'); } catch {}
    if (!Array.isArray(fonts) || !fonts.length) {
      fonts = ['Lato','Roboto','Alegreya','Bebas Neue','Cantata One','Damion','Old Standard TT','Audiowide','Josefin Sans','Rockwell','Comic Sans MS'];
      if (['10345021800645','10345021866181','10345021997253'].includes(trigger.dataset.pwProductId)) {
        fonts = ['Arial Black','Bradley Hand','Comic Sans MS','Marker Felt','Party LET','Times New Roman','Rockwell','SnellRoundhandScript'];
      }
    }
    const selectedFont = properties['Engraving font'] || fonts[0];
    if (!fonts.includes(selectedFont)) fonts.unshift(selectedFont);
    const fontSelect = this.form.elements.namedItem('font');
    const current = this.querySelector('[data-pw-font-current]');
    const picker = this.querySelector('.pw-cart-font-picker');
    const options = this.querySelector('[data-pw-font-options]');
    const chooseFont = (font) => {
      fontSelect.value = font;
      current.textContent = font;
      current.style.fontFamily = '"' + font + '", serif';
      options.querySelectorAll('button').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.font === font)));
      picker.open = false;
    };
    options.replaceChildren(...fonts.map(font => {
      const button = document.createElement('button');
      button.type = 'button'; button.dataset.font = font; button.textContent = font;
      button.style.fontFamily = '"' + font + '", serif';
      button.addEventListener('click', () => { chooseFont(font); picker.querySelector('summary').focus(); });
      return button;
    }));
    chooseFont(selectedFont);
    const maximum = Number(trigger.dataset.pwMaximum) || 15;
    for (let index = 1; index <= 3; index++) {
      this.querySelector(`[data-pw-editor-field="${index}"]`).hidden = index > this.lineCount;
      const input = this.form.elements.namedItem(`line${index}`);
      input.value = properties[`Engraving line ${index}`] || '';
      input.maxLength = Math.max(maximum, input.value.length);
    }
    this.dialog.showModal();
    this.form.elements.namedItem('line1').focus();
  }

  async save() {
    if (this.busy) return;
    const values = Array.from({ length: this.lineCount }, (_, index) => this.form.elements.namedItem(`line${index + 1}`).value.trim());
    if (values.every(value => !value) && !window.confirm(this.dataset.blankConfirm)) return;
    this.busy = true;
    this.form.querySelectorAll('button').forEach(button => { button.disabled = true; });
    const deferred = CartLinesUpdateEvent.createPromise();
    deferred.promise.catch(() => {});
    try {
      const root = window.Shopify?.routes?.root || '/';
      const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) throw new Error(this.dataset.saveError);
      const cart = await cartResponse.json();
      const line = cart.items.find(item => item.key === this.lineKey);
      if (!line) throw new Error(this.dataset.saveError);
      const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(node => node.dataset.sectionId))].slice(0, 5);
      const response = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: line.key, quantity: line.quantity, properties: engravingProperties(line.properties, values, this.lineCount, this.form.elements.namedItem('font').value), sections, sections_url: window.location.pathname }),
      });
      let result = await response.json();
      if (!response.ok || result.errors || result.status) throw new Error(this.dataset.saveError);
      // Keep only explicitly linked, matching-engraving harnesses in sync.
      const setId = line.properties?.['_Pawture set'];
      const matchingHarnesses = setId ? cart.items.filter(item =>
        item.key !== line.key &&
        item.properties?.['_Pawture set'] === setId &&
        item.properties?.['Harness engraving'] === 'Match collar engraving'
      ) : [];
      for (const harness of matchingHarnesses) {
        const syncResponse = await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: harness.key, quantity: harness.quantity,
            properties: engravingProperties(harness.properties, values, this.lineCount, this.form.elements.namedItem('font').value),
            sections, sections_url: window.location.pathname
          }),
        });
        const synced = await syncResponse.json();
        if (!syncResponse.ok || synced.errors || synced.status) throw new Error(this.dataset.saveError);
        result = synced;
      }
      this.dispatchEvent(new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [{ id: line.key, quantity: line.quantity }], promise: deferred.promise }));
      this.dialog.close();
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(result), detail: { sections: result.sections, items: result.items, itemCount: result.item_count, source: 'pawture-cart-editor', didError: false } });
    } catch (error) {
      deferred.reject(error);
      this.querySelector('[data-pw-editor-error]').textContent = this.dataset.saveError;
    } finally {
      this.busy = false;
      this.form.querySelectorAll('button').forEach(button => { button.disabled = false; });
    }
  }
}

if (!customElements.get('pawture-cart-editor')) customElements.define('pawture-cart-editor', PawtureCartEditor);
